import React, { useEffect, useState } from 'react';
import {
  Button,
  Descriptions,
  Drawer,
  Input,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  OrderDto,
  OrderStatus,
  PaymentStatus,
  useUpdateOrderStatus,
} from '@shared/api/hooks/useOrders';

interface Props {
  order: OrderDto | null;
  open: boolean;
  onClose: () => void;
  onRefund: (order: OrderDto) => void;
  statusLabel: Record<OrderStatus, string>;
  statusColor: Record<OrderStatus, string>;
  paymentLabel: Record<PaymentStatus, string>;
  paymentColor: Record<PaymentStatus, string>;
}

export function OrderDetailDrawer({
  order,
  open,
  onClose,
  onRefund,
  statusLabel,
  statusColor,
  paymentLabel,
  paymentColor,
}: Props): React.ReactElement {
  const updateOrder = useUpdateOrderStatus();
  const [courier, setCourier] = useState('');
  const [tracking, setTracking] = useState('');
  // T-234 — recapito telefonico, correggibile dal dettaglio ordine.
  const [phone, setPhone] = useState('');

  useEffect(() => {
    setCourier(order?.courierName ?? '');
    setTracking(order?.trackingNumber ?? '');
    setPhone(order?.contactPhone ?? '');
  }, [order?.id, order?.courierName, order?.trackingNumber, order?.contactPhone]);

  if (!order) {
    return <Drawer open={open} onClose={onClose} />;
  }

  const refundable =
    order.paymentIntentId != null && order.paymentStatus !== 'REFUNDED';

  const isShipping = order.fulfillmentMethod === 'SHIPPING';
  // Tracking can be set/edited while the order is still CONFIRMED or already SHIPPED.
  const canEditTracking =
    isShipping && (order.status === 'CONFIRMED' || order.status === 'SHIPPED');

  const phoneChanged = phone.trim() !== (order.contactPhone ?? '');

  const onSavePhone = async (): Promise<void> => {
    if (!phone.trim()) {
      message.warning('Inserisci un recapito telefonico');
      return;
    }
    try {
      await updateOrder.mutateAsync({ id: order.id, contactPhone: phone.trim() });
      message.success('Recapito telefonico aggiornato');
    } catch (e) {
      // Il backend valida il formato (400 con messaggio in italiano).
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Salvataggio recapito telefonico fallito';
      message.error(msg);
    }
  };

  const onSaveTracking = async (): Promise<void> => {
    if (!courier.trim() || !tracking.trim()) {
      message.warning('Inserisci corriere e codice tracking');
      return;
    }
    try {
      await updateOrder.mutateAsync({
        id: order.id,
        courierName: courier.trim(),
        trackingNumber: tracking.trim(),
        // First time tracking is entered → mark the order as shipped.
        ...(order.status === 'CONFIRMED' ? { status: 'SHIPPED' as OrderStatus } : {}),
      });
      message.success(
        order.status === 'CONFIRMED' ? 'Ordine segnato come spedito' : 'Tracking aggiornato',
      );
    } catch {
      message.error('Salvataggio tracking fallito');
    }
  };

  return (
    <Drawer
      title={
        <Space size="small">
          <Typography.Text code copyable>
            {order.pickupQrCode}
          </Typography.Text>
          <Tag color={statusColor[order.status]}>{statusLabel[order.status]}</Tag>
          <Tag color={paymentColor[order.paymentStatus]}>
            {paymentLabel[order.paymentStatus]}
          </Tag>
        </Space>
      }
      width={560}
      open={open}
      onClose={onClose}
      extra={
        refundable && (
          <Button danger onClick={() => onRefund(order)}>
            Rimborsa
          </Button>
        )
      }
    >
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="ID">
          <Typography.Text code copyable>
            {order.id}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Creato">
          {dayjs(order.createdAt).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        {order.paidAt && (
          <Descriptions.Item label="Pagato">
            {dayjs(order.paidAt).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Fulfillment">
          {order.fulfillmentMethod === 'SHIPPING' ? 'Spedizione' : 'Ritiro in store'}
        </Descriptions.Item>
        <Descriptions.Item label="Metodo pagamento">
          {order.paymentMethod}
        </Descriptions.Item>
        {order.paymentIntentId && (
          <Descriptions.Item label="Stripe PaymentIntent">
            <Typography.Text code copyable>
              {order.paymentIntentId}
            </Typography.Text>
          </Descriptions.Item>
        )}
        {order.stripeChargeId && (
          <Descriptions.Item label="Stripe Charge">
            <Typography.Text code copyable>
              {order.stripeChargeId}
            </Typography.Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      {order.shippingAddress && (
        <Descriptions
          size="small"
          column={1}
          bordered
          title="Indirizzo spedizione"
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label={order.shippingAddress.label}>
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{order.shippingAddress.recipient}</Typography.Text>
              <Typography.Text>
                {order.shippingAddress.line1}
                {order.shippingAddress.line2 ? `, ${order.shippingAddress.line2}` : ''}
              </Typography.Text>
              <Typography.Text>
                {order.shippingAddress.postalCode} {order.shippingAddress.city} ·{' '}
                {order.shippingAddress.province}
              </Typography.Text>
              <Typography.Text type="secondary">
                {order.shippingAddress.phone}
              </Typography.Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>
      )}

      <Descriptions
        size="small"
        column={1}
        bordered
        title="Recapito telefonico"
        style={{ marginTop: 16 }}
      >
        <Descriptions.Item label="Telefono">
          {order.contactPhone ? (
            <Typography.Link href={`tel:${order.contactPhone}`}>
              {order.contactPhone}
            </Typography.Link>
          ) : (
            <Typography.Text type="secondary">Non fornito</Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Correggi">
          <Space.Compact style={{ width: '100%' }}>
            <Input
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              placeholder="Es. +39 333 1234567"
              value={phone}
              maxLength={25}
              onChange={(e) => setPhone(e.target.value)}
              onPressEnter={() => {
                if (phoneChanged) void onSavePhone();
              }}
            />
            <Button
              type="primary"
              loading={updateOrder.isPending}
              disabled={!phoneChanged}
              onClick={onSavePhone}
            >
              Salva
            </Button>
          </Space.Compact>
        </Descriptions.Item>
      </Descriptions>

      {isShipping && (
        <Descriptions
          size="small"
          column={1}
          bordered
          title="Spedizione & tracking"
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="Corriere">
            {canEditTracking ? (
              <Input
                placeholder="Es. BRT, GLS, Poste Italiane"
                value={courier}
                maxLength={100}
                onChange={(e) => setCourier(e.target.value)}
              />
            ) : (
              order.courierName ?? '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Codice tracking">
            {canEditTracking ? (
              <Input
                placeholder="Codice di tracciamento"
                value={tracking}
                maxLength={100}
                onChange={(e) => setTracking(e.target.value)}
              />
            ) : order.trackingNumber ? (
              <Typography.Text code copyable>
                {order.trackingNumber}
              </Typography.Text>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          {order.shippedAt && (
            <Descriptions.Item label="Spedito il">
              {dayjs(order.shippedAt).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
          )}
          {canEditTracking && (
            <Descriptions.Item label=" ">
              <Button
                type="primary"
                loading={updateOrder.isPending}
                onClick={onSaveTracking}
              >
                {order.status === 'CONFIRMED'
                  ? 'Salva e segna spedito'
                  : 'Aggiorna tracking'}
              </Button>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}

      <Typography.Title level={5} style={{ marginTop: 16 }}>
        Prodotti
      </Typography.Title>
      <Table
        size="small"
        // T-137 — la chiave DEVE includere la personalizzazione: due unità dello
        // stesso prodotto+taglia con nome/numero diversi (due maglie, due nomi)
        // sono righe distinte. Con la vecchia chiave `${productId}-${size}` React
        // le collassava — proprio gli ordini che questa vista serve a leggere.
        rowKey={(it) =>
          `${it.productId}-${it.size}-${it.customName ?? ''}-${it.customNumber ?? ''}`
        }
        dataSource={order.items}
        pagination={false}
        columns={[
          {
            title: 'Prodotto',
            render: (_v, it) => {
              const n = it.productName ?? {};
              return n.it ?? n.en ?? '(senza nome)';
            },
          },
          { title: 'Taglia', dataIndex: 'size', width: 80 },
          {
            // T-137 — chi evade la spedizione DEVE vedere nome/numero da stampare.
            // Salta all'occhio sulle righe personalizzate (tag colorato); resta
            // silenziosa su quelle senza (— attenuato), niente rumore.
            title: 'Personalizzazione',
            render: (_v, it) => {
              const hasName = it.customName != null && it.customName !== '';
              // customNumber può essere 0 → test esplicito su null, mai falsy.
              const hasNumber = it.customNumber != null;
              if (!hasName && !hasNumber) {
                return <Typography.Text type="secondary">—</Typography.Text>;
              }
              const label = [hasName ? it.customName : null, hasNumber ? `#${it.customNumber}` : null]
                .filter(Boolean)
                .join(' ');
              return (
                <Space direction="vertical" size={0}>
                  <Tag color="gold" style={{ fontWeight: 600, margin: 0 }}>
                    {label}
                  </Tag>
                  {it.customizationFee > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      +{it.customizationFee.toFixed(2)} {order.currency}
                    </Typography.Text>
                  )}
                </Space>
              );
            },
          },
          { title: 'Q', dataIndex: 'quantity', width: 60, align: 'right' },
          {
            title: 'Subtotale',
            dataIndex: 'subtotal',
            width: 110,
            align: 'right',
            render: (v: number) => `${v.toFixed(2)} ${order.currency}`,
          },
        ]}
      />

      <Descriptions
        size="small"
        column={1}
        bordered
        title="Importi"
        style={{ marginTop: 16 }}
      >
        <Descriptions.Item label="Subtotale">
          {order.subtotal.toFixed(2)} {order.currency}
        </Descriptions.Item>
        {order.discountAmount > 0 && (
          <Descriptions.Item label="Sconto">
            − {order.discountAmount.toFixed(2)} {order.currency}
          </Descriptions.Item>
        )}
        {order.shippingCost > 0 && (
          <Descriptions.Item label="Spedizione">
            {order.shippingCost.toFixed(2)} {order.currency}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Totale">
          <Typography.Text strong>
            {order.finalAmount.toFixed(2)} {order.currency}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="IVA inclusa">
          {order.vatAmount.toFixed(2)} {order.currency}
        </Descriptions.Item>
        {order.refundedAmount > 0 && (
          <Descriptions.Item label="Rimborsato">
            <Typography.Text type="danger">
              − {order.refundedAmount.toFixed(2)} {order.currency}
            </Typography.Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      {order.notes && (
        <Descriptions
          size="small"
          column={1}
          bordered
          title="Note"
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="Cliente">{order.notes}</Descriptions.Item>
        </Descriptions>
      )}
    </Drawer>
  );
}
